import React from 'react';
import {
  NotificationQueueItem,
  NotificationType,
  NotificationRenderFunction,
} from '../../redux/types';
import FaveNotification from '../../../FaveButton/FaveNotification';
import CollectNotification from '../../../CollectionPanel/CollectNotification';
import CommentDeleteNotification from '../../../CommentDeleteNotification';
import GroupAddedNotification from '../../../AddToGroupsPanel/_partials/GroupAddedNotification';
import LiveNotification from '../../../LiveNotifications/LiveNotification';
import BadgeNotification from '../../../coreBadge/BadgeNotification';
import GenericNotification from '../../GenericNotification';
import GenericUndoNotification from '../../GenericUndoNotification';
import DeviationUnpublishedNotification from '../../../DeviationUnpublishedNotification';

export interface Props {
  notification?: NotificationQueueItem;
  onlyUseRenderFunction?: boolean;
  children?: NotificationRenderFunction;
  removeNotification: (id: number) => void;
}

export interface NotificationAnimatorProps {
  queue: NotificationQueueItem[];
  notificationProps: Omit<Props, 'notification'>;
  className?: string;
}

const NotificationContent: React.FC<Props> = ({
  notification,
  onlyUseRenderFunction,
  removeNotification,
  children,
}) => {
  if (!notification) {
    return null;
  }

  // this can be helpful when you have two Notifications components but
  // want each to display different types of notifications (without both duplicating all)
  if (onlyUseRenderFunction) {
    return children ? <>{children(notification, removeNotification)}</> : null;
  }

  switch (notification.type) {
    case NotificationType.Collect:
    case NotificationType.CollectPrivate:
    case NotificationType.CollectUndo:
      return (
        <CollectNotification
          id={notification.id}
          queueName={notification.queueName}
          type={notification.type}
          removeNotification={removeNotification}
          {...notification.params}
        />
      );
    case NotificationType.Fave:
    case NotificationType.FaveUndo:
      return (
        <FaveNotification
          id={notification.id}
          queueName={notification.queueName}
          type={notification.type}
          removeNotification={removeNotification}
          {...notification.params}
        />
      );
    case NotificationType.DeleteComment:
      return (
        <CommentDeleteNotification
          id={notification.id}
          queueName={notification.queueName}
          type={notification.type}
          removeNotification={removeNotification}
          {...notification.params}
        />
      );
    case NotificationType.AddToGroup:
      return (
        <GroupAddedNotification
          id={notification.id}
          queueName={notification.queueName}
          {...notification.params}
        />
      );
    case NotificationType.Unpublish:
      return (
        <DeviationUnpublishedNotification
          id={notification.id}
          queueName={notification.queueName}
          {...notification.params}
        />
      );
    case NotificationType.Live:
      return (
        <LiveNotification
          id={notification.id}
          queueName={notification.queueName}
          notificationHtml={notification.params.message}
          biData={notification.params.bi_data}
          linkTo={notification.params.url}
          {...notification.params}
        />
      );
    case NotificationType.Generic:
      return (
        <GenericNotification
          id={notification.id}
          queueName={notification.queueName}
          {...notification.params}
        />
      );
    case NotificationType.GenericUndo:
      return (
        <GenericUndoNotification
          id={notification.id}
          queueName={notification.queueName}
          {...notification.params}
        />
      );
    case NotificationType.Badge:
      return (
        <BadgeNotification
          id={notification.id}
          queueName={notification.queueName}
          removeNotification={removeNotification}
          {...notification.params}
        />
      );
    default:
      if (!children) {
        return null;
      }
      return <>{children(notification, removeNotification)}</>;
  }
};
NotificationContent.displayName = 'NotificationContent';

export default NotificationContent;
